/**
 * Weapon definitions shared by client (tooltips, kill feed) and server (authority).
 * All numbers are deterministic data; the server is the only place damage is applied.
 *
 * Phase 2c replaced the hitscan gun table with six melee/caster **stat sticks** — three per
 * playable Calling. There is no rpm, magazine, spread, recoil or falloff any more, because there
 * is no firing model any more: a weapon is a damage range, a swing time and a reach, and the
 * auto-attack loop reads exactly those three numbers.
 *
 * `WeaponDef.id` is the item's `look` id (1..6, in the crowd bake's frozen order), so
 * `WEAPONS[snap.weaponId]` and `unpackLook(snap.look).weapon` name the same object. That identity
 * is the whole reason the ids are small integers.
 */
import {
  CLASS_WEAPONS,
  ITEMS,
  equippedStatLines,
  type ItemStack,
  type RarityLevel,
} from './items.js';
import type { GearStatLine } from './stats.js';

export interface WeaponDef {
  /** 1..6. Also the item's look id and the atlas cell index. */
  id: number;
  name: string;
  /** Stable `ITEMS` key. */
  itemId: string;
  /** Damage per swing, inclusive; the server rolls uniformly inside it. */
  damage: [min: number, max: number];
  /** Milliseconds between auto-attack swings. */
  speedMs: number;
  /** Reach in metres. */
  range: number;
  rarity: RarityLevel;
  /** Calling restriction (`callings.CallingId`), or 0 for open issue. */
  calling: number;
  /** Character level required to wield it. */
  levelReq: number;
}

export const WEAPON_NONE = 0;
/**
 * Not a weapon: the "weaponId" an `Ev.Death` carries when the ground did it (a fall). u8 max so it
 * can never collide with a real `WeaponDef` id; the kill feed draws a falling figure for it.
 */
export const WEAPON_FALL = 255;

export const WEAPONS: Record<number, WeaponDef> = Object.fromEntries(
  CLASS_WEAPONS.map((w) => [
    w.look,
    {
      id: w.look,
      name: w.name,
      itemId: w.id,
      damage: [w.damage[0], w.damage[1]] as [number, number],
      speedMs: w.speedMs,
      range: w.range,
      rarity: w.rarity,
      calling: w.calling,
      levelReq: w.levelReq,
    } satisfies WeaponDef,
  ]),
);

/**
 * Unarmed. Everybody has fists, and a character who has just broken their only weapon must still
 * be able to swing at something — a level-1 mob dies to this eventually, which is the point.
 */
export const UNARMED: WeaponDef = {
  id: WEAPON_NONE,
  name: 'Bare hands',
  itemId: '',
  damage: [2, 4],
  speedMs: 2000,
  range: 2.5,
  rarity: 0,
  calling: 0,
  levelReq: 1,
};

/** The `WeaponDef` for a look/weapon id, falling back to `UNARMED` rather than throwing. */
export const weaponById = (id: number): WeaponDef => WEAPONS[id] ?? UNARMED;

/** The `WeaponDef` an item id names, or undefined if it is not a weapon. */
export function weaponForItem(itemId: string): WeaponDef | undefined {
  const wid = ITEMS[itemId]?.weaponId;
  return wid === undefined ? undefined : WEAPONS[wid];
}

/** Every weapon a Calling may carry, worst rung first. */
export function weaponsForCalling(calling: number): WeaponDef[] {
  return Object.values(WEAPONS)
    .filter((w) => w.calling === calling)
    .sort((a, b) => a.rarity - b.rarity || a.id - b.id);
}

/** Mean damage of a swing — what a tooltip's "damage per second" line divides by `speedMs`. */
export const averageDamage = (w: WeaponDef): number => (w.damage[0] + w.damage[1]) / 2;

/** Damage per second, to one decimal. The number the paperdoll compares two weapons with. */
export const dps = (w: WeaponDef): number =>
  Math.round(((averageDamage(w) / w.speedMs) * 1000 + Number.EPSILON) * 10) / 10;

/**
 * One swing's damage, rolled uniformly inside the range with an injected RNG so the server can
 * seed it and the tests are exact.
 */
export function rollWeaponDamage(w: WeaponDef, rng: () => number = Math.random): number {
  const [lo, hi] = w.damage;
  return lo + Math.floor(rng() * (hi - lo + 1));
}

/**
 * **The one call the combat side needs.** Everything a character is currently getting value from,
 * in `GearStatLine` shape, ready for `computeStats(level, calling, equippedGearStats(items))`.
 *
 * Broken pieces (0 durability) are already excluded — that filtering lives in `equippedFor`, so no
 * caller has to know durability exists. This wrapper is here rather than in `items.ts` only
 * because it is the layer that can see `WEAPONS`; `items.ts` cannot, without a cycle.
 */
export function equippedGearStats(stacks: readonly ItemStack[]): GearStatLine[] {
  return equippedStatLines(stacks, (id) => WEAPONS[id]);
}

/** Time a dead player waits before respawning. Persistent maps use Release instead; see room.ts. */
export const RESPAWN_DELAY_MS = 3000;
